import { useEffect, useRef } from "preact/hooks";
import {
  css,
  listById,
  moveList,
  renameList,
  required,
  setListEmoji,
  ToucanManageRows,
  ToucanSheetHeader,
  useDialog,
} from "toucan";
import {
  addShelf,
  albumsOnShelf,
  deleteShelf,
  RESERVED_SHELF_NAMES,
} from "../model.ts";
import { forgetLibraryShelf } from "../routes.ts";
import { shelves, store } from "../state.ts";
import type { ManageRowActions, ManageRowItem } from "toucan";

/* The full-screen sheet strips its article's padding so each page pads its own
   contents. The manage screen inside needs that room back on all sides. */
const rows = css`
  padding: 1rem;
`;

/** The shelf a row names. Every row is drawn from a shelf that is there. */
function shelf(id: string) {
  return required(listById(store().shelves, id), "shelf");
}

/** What a row's controls mean here. */
const actions: ManageRowActions = {
  onRename: (id, name) => {
    renameList(shelf(id), name);
  },
  onEmoji: (id, emoji) => {
    setListEmoji(shelf(id), emoji);
  },
  onDelete: (id) => {
    deleteShelf(store(), id);
    // Closing returns to the library view this was opened from, which must
    // not be the deleted shelf.
    forgetLibraryShelf(id);
  },
  onMove: (id, delta) => {
    moveList(store().shelves, id, delta);
  },
  onCreate: (name, emoji) => {
    if (emoji === "") {
      addShelf(store(), name);
      return;
    }
    addShelf(store(), name, emoji);
  },
};

/**
 * The Manage shelves screen: the platform's `ToucanManageRows`, told how many
 * albums are on each shelf and what each row action does to the library.
 */
export function ShelvesDialog({ onClose }: { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  useDialog(dialog, onClose);
  useEffect(() => {
    // showModal() focuses the first control, the back arrow; focus the dialog
    // itself so nothing is selected until the person moves there.
    required(dialog.current, "dialog").focus();
  }, []);

  const items: ManageRowItem[] = shelves().map((entry) => {
    const item: ManageRowItem = {
      id: entry.id,
      name: entry.name,
      uses: albumsOnShelf(store(), entry.id).length,
    };
    if (entry.emoji !== undefined) {
      item.emoji = entry.emoji;
    }
    return item;
  });

  return (
    <dialog ref={dialog} class="sheet-page" tabIndex={-1} data-screen="shelves">
      <article>
        <ToucanSheetHeader heading="Manage shelves" onBack={onClose} />
        <ToucanManageRows
          class={rows}
          thing="Shelf"
          noun="album"
          reorder
          items={items}
          actions={actions}
          reserved={RESERVED_SHELF_NAMES}
        />
      </article>
    </dialog>
  );
}
